import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * T-045 (11.09.2026): Frontend-Versionswaechter.
 *
 * Hintergrund: Die Kundin sah mehrfach veraltete Oberflaechen (T-028/T-038 Safari-Cache der index.html,
 * 11.09. "Keine Termine 2027" im Kundenkalender), weil ein alter Bundle-Stand im Browser weiterlief.
 * Ein manueller Hard-Reload ist auf iPad/Mac Safari unhandlich.
 *
 * Mechanik: Der Waechter liest das aktuell geladene Bundle (script src /static/js/main.<hash>.js) und
 * vergleicht es mit dem Bundle, das der Server in /asset-manifest.json nennt (Cache-Buster im Query,
 * cache: no-store). Weicht es ab, wird die Seite neu geladen, und zwar nur zu "sicheren" Zeitpunkten:
 * beim Start, bei einem Routenwechsel und wenn der Tab wieder sichtbar wird. Ein reiner Zeitgeber
 * (alle 5 Minuten) merkt sich nur, dass ein neuer Stand vorliegt, und laedt beim naechsten sicheren
 * Zeitpunkt neu, damit niemand mitten in einem Formular unterbrochen wird.
 *
 * Schleifenschutz: hoechstens ein automatischer Reload je 2 Minuten (sessionStorage), falls
 * asset-manifest.json und index.html einmal nicht zusammenpassen (z.B. waehrend eines Deploys).
 */

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_GUARD_MS = 2 * 60 * 1000;
const GUARD_KEY = 'ff-version-reload-at';

const getLoadedMainBundle = (): string | null => {
  try {
    const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
    for (const s of scripts) {
      const m = s.src.match(/\/static\/js\/(main\.[0-9a-f]+\.js)/i);
      if (m) return m[1];
    }
  } catch {
    // ignorieren
  }
  return null;
};

const fetchServerMainBundle = async (): Promise<string | null> => {
  try {
    const resp = await fetch(`/asset-manifest.json?_v=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
    });
    if (!resp.ok) return null;
    const manifest = await resp.json();
    const mainJs: string | undefined = manifest?.files?.['main.js'];
    if (!mainJs) return null;
    const m = mainJs.match(/(main\.[0-9a-f]+\.js)/i);
    return m ? m[1] : null;
  } catch {
    return null;
  }
};

const canReloadNow = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(GUARD_KEY) || '0');
    return !last || Date.now() - last > RELOAD_GUARD_MS;
  } catch {
    return true;
  }
};

const markReload = () => {
  try {
    sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {
    // ignorieren
  }
};

const VersionWatcher: React.FC = () => {
  const location = useLocation();
  const staleRef = useRef(false);
  const loadedRef = useRef<string | null>(null);

  // Prueft den Serverstand. reloadIfStale=true: sofort neu laden, sonst nur merken.
  const check = async (reloadIfStale: boolean) => {
    if (!loadedRef.current) loadedRef.current = getLoadedMainBundle();
    if (!loadedRef.current) return; // Dev-Server (kein gehashtes Bundle): nichts tun
    const serverBundle = await fetchServerMainBundle();
    if (!serverBundle) return;
    if (serverBundle !== loadedRef.current) {
      staleRef.current = true;
      // eslint-disable-next-line no-console
      console.info(`[VersionWatcher] Neuer Frontend-Stand auf dem Server: ${serverBundle} (geladen: ${loadedRef.current})`);
      if (reloadIfStale && canReloadNow()) {
        markReload();
        window.location.reload();
      }
    }
  };

  // Start: sofort pruefen
  useEffect(() => {
    void check(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Routenwechsel: sicherer Zeitpunkt fuer einen Reload
  useEffect(() => {
    if (staleRef.current && canReloadNow()) {
      markReload();
      window.location.reload();
      return;
    }
    void check(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Zeitgeber (nur merken) und Sichtbarkeit (neu laden, wenn der Tab zurueckkommt)
  useEffect(() => {
    const timer = window.setInterval(() => { void check(false); }, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void check(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default VersionWatcher;
